import { ContentTypeEnum } from '@elearning-platform/api-types/enums';
import { ContentsUnion } from '@elearning-platform/api-types/interfaces';
import { useEffect, useMemo } from 'react';
import { Location, useLocation } from 'react-router';

import { contentHashRoutes } from '../../../consts';
import { AppLanguage } from '../../../enums';
import { useAppLanguage, useRedirectTo } from '../../../hooks';
import { UseRedirectTo } from '../../../interfaces';
import { getContentHash } from '../../../utils';
import { HashRoute } from '../interfaces';
import { hashRegexp, legacyHashRegexp } from '../regexps';

export const useDrawerContent: (allContents: ContentsUnion[]) => ContentsUnion | undefined = (
  allContents: ContentsUnion[]
): ContentsUnion | undefined => {
  const { hash }: Location = useLocation();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const language: AppLanguage = useAppLanguage();

  useEffect(
    (): void => {
      const legacyMatch: RegExpMatchArray | null = hash.match(legacyHashRegexp);
      if (!legacyMatch || !allContents.length) {
        return;
      }

      const item: ContentsUnion | undefined = allContents.find(
        (content: ContentsUnion): boolean => content.id === Number(legacyMatch[1]) && content.type === ContentTypeEnum.contents
      );
      const newHash: string | undefined = item ? getContentHash(item, language) : undefined;

      if (item) {
        redirect({ hash: newHash, replace: true });
      }
    },
    [allContents, hash, redirect, language]
  );

  return useMemo(
    (): ContentsUnion | undefined => {
      const match: RegExpMatchArray | null = hash.match(hashRegexp);
      if (!match) {
        return undefined;
      }

      const id: number = Number(match[2]);
      const route: HashRoute | undefined = Object.values(contentHashRoutes).flat().find(
        (hashRoute: HashRoute): boolean => hashRoute.prefix === match[1]
      );

      if (!route) {
        return undefined;
      }

      return allContents.find((item: ContentsUnion): boolean => item.id === id && item.type === route.type);
    },
    [allContents, hash]
  );
};
